import React from 'react';
import { TripResponse } from '../../store/backendApi';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { HeaderOptionsPage } from './HeaderOptionsPage';
import { SharingOptionsPage } from './SharingOptionsPage';
import { Button } from '../../components/Button';

export type SidebarSection = 'trip-details' | 'privacy-sharing' | null;

export interface EditTripSidebarProps {
  tripData: TripResponse | undefined;
  section: SidebarSection;
  onClose: () => void;
}

const getSectionTitle = (section: SidebarSection): string => {
  switch (section) {
    case 'trip-details':
      return 'Trip Details';
    case 'privacy-sharing':
      return 'Privacy & Sharing';
    default:
      return 'Settings';
  }
};

const getSectionEmoji = (section: SidebarSection): string => {
  switch (section) {
    case 'trip-details':
      return '🌍';
    case 'privacy-sharing':
      return '🔒';
    default:
      return '⚙️';
  }
};

export const EditTripSidebar = (props: EditTripSidebarProps) => {
  const isOpen = props.section !== null;

  if (!props.tripData) {
    return null;
  }

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={props.onClose}
      />
      <div
        className={`fixed top-0 right-0 h-full w-[420px] max-w-[90vw] bg-surface shadow-2xl z-50 
          transform transition-transform duration-300 ease-in-out ${
    isOpen ? 'translate-x-0' : 'translate-x-full'
    }`}
      >
        <div className="bg-primary border-brand px-6 py-5">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-on-dark flex items-center gap-2">
              <span className="text-2xl">{getSectionEmoji(props.section)}</span>
              {getSectionTitle(props.section)}
            </h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-lg p-1.5"
              onClick={props.onClose}
            >
              <XMarkIcon className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto h-[calc(100%-140px)] px-6 py-6">
          {props.section === 'trip-details' && <HeaderOptionsPage tripData={props.tripData} />}
          {props.section === 'privacy-sharing' && <SharingOptionsPage trip={props.tripData} />}
        </div>

        {/* Footer Note */}
        <div className="absolute bottom-0 left-0 right-0 bg-canvas px-6 py-4">
          <p className="text-xs text-muted text-center">
            {props.tripData.unpublishedChanges ? (
              <span className="flex items-center justify-center gap-1">
                <span className="w-2 h-2 bg-warning-subtle rounded-full animate-pulse"></span>
                You have unpublished changes
              </span>
            ) : (
              <span className="flex items-center justify-center gap-1">
                <span className="w-2 h-2 bg-success-subtle rounded-full"></span>
                Everything is up to date
              </span>
            )}
          </p>
        </div>
      </div>
    </>
  );
};
